'use client';

import { UrlObject } from 'url';
import { Fragment, ForwardedRef, forwardRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import {
  TLanguagesConfigItem,
  TIconId,
  TLocale,
  isLangRTL,
} from '@northladder/utilities';

import { useHrefOptions } from '../../../hooks';
import { Link } from '../../../routing';

import { FlagIcon } from '../../Flags/flagIcons';

interface IMenuItemLinkProps {
  /**
   * The final path to be passed to the underlying `<a>` link tag.
   */
  path: UrlObject | string;
  /**
   * Used to determine the styling of this based on additional parent logic.
   */
  isCurrent: boolean;
  /**
   * The title/name of the item to be displayed on the UI.
   */
  label: string;
  /**
   * The locale to be passed to the Link
   */
  locale: TLocale;
  /**
   * The id of the icon/flag to render besides the label
   */
  icon: TIconId;
}

type TMenuItemLinkRef = ForwardedRef<HTMLAnchorElement>;

/**
 * -----------------------------------------------------------------------------
 * The individual menu item displaying an icon/flag alongside the label.
 */
const MenuItemLink = forwardRef(
  (
    { path, icon, isCurrent, label, locale, ...rest }: IMenuItemLinkProps,
    ref: TMenuItemLinkRef
  ) => {
    const rtlClass = isLangRTL(locale) ? 'font-primary-ar' : 'font-primary';

    return (
      <Link
        ref={ref}
        className={clsx(
          `dark:text-primary-100 hover:text-primary-600 drop-down-item
          data-focus:last-of-type:rounded-b-xl data-focus:bg-primary-50
          data-focus:dark:bg-primary-950/50 data-focus:dark:text-primary-300
          data-focus:text-primary-900 flex cursor-pointer items-center gap-2
          rounded-none p-1 px-2 text-xs font-normal text-gray-700 transition-all
          duration-300 dark:bg-neutral-800/70
          `,
          rtlClass
        )}
        locale={locale}
        {...rest}
        href={path}
      >
        <FlagIcon className="rounded-md" id={icon} size={22} />
        {label}
      </Link>
    );
  }
);

MenuItemLink.displayName = 'MenuItemLink';

interface ILocaleMenuProps {
  /**
   * A list of items for the dropdown.
   */
  items: TLanguagesConfigItem[];
}

/**
 * -----------------------------------------------------------------------------
 * This provides a menu dropdown that helps in changing the active locale for
 * the app.
 * - It displays the country flag alongside the locale name.
 */
export function LocaleMenu({ items }: ILocaleMenuProps) {
  const { href, locale, activeLanguage } = useHrefOptions();

  if (!activeLanguage) return null;

  return (
    <Menu as="div">
      <Menu.Button
        className="
          rtl:text-md font-primary-ar ltr:font-primary shadow-xs focus:outline-hidden
          data-active:rounded-t-2xl data-active:rounded-b-none dark:focus:ring-primary-400/50
          dark:focus:ring-offset-primary-800/10 focus:ring-primary-100
          dark:focus-ring-primary-100 focus:hover:bg-primary-200 inline-flex w-full
          cursor-pointer items-center justify-between gap-2 rounded-3xl border
          border-gray-200 bg-white p-2 text-xs font-normal text-gray-700 transition-all
          hover:bg-neutral-50 focus:ring-2  focus:ring-offset-1 focus:ring-offset-gray-100
          dark:border-gray-700/50 dark:bg-neutral-800/70 dark:text-gray-300
          dark:hover:bg-neutral-800 focus:dark:hover:bg-neutral-950
        "
      >
        <FlagIcon
          className="shrink-0 rounded-lg"
          id={activeLanguage.icon}
          size={22}
        />
        <span>{activeLanguage.label}</span>
        <ChevronDownIcon
          aria-hidden="true"
          className="size-5 shrink-0 stroke-0"
        />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className="
            focus:outline-hidden absolute right-0 z-10 mt-0 w-full origin-top-right
            rounded-b-2xl border-t-0 bg-white shadow-lg ring-1 ring-black/5
            dark:border dark:border-gray-700/40 dark:bg-neutral-950
          "
        >
          <div className="divide-y divide-gray-50 py-1 dark:divide-gray-800">
            {items.map((item) => {
              const isCurrentLocale = item.locale === locale;

              return (
                <Menu.Item key={item.id}>
                  <MenuItemLink
                    key={item.id}
                    icon={item.icon}
                    isCurrent={isCurrentLocale}
                    label={item.label}
                    locale={item.id}
                    path={href}
                  />
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
